/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { AnalyticsLogger } from "./AnalyticsLogger";
import type { LogDetails } from "../types";

export const ProfileScreen = {
  SelectProfile: "SelectProfile",
  ManageProfiles: "ManageProfiles",
  ProfileDetails: "ProfileDetails",
  Settings: "Settings"
} as const;

export type ProfileScreenName =
  (typeof ProfileScreen)[keyof typeof ProfileScreen];

export const ProfileAction = {
  SelectProfile: "select_profile",
  CreateProfile: "create_profile",
  UpdateProfile: "update_profile",
  DeleteProfile: "delete_profile",
  SwitchProfile: "switch_profile",
  AddProfileSelected: "add_profile_selected",
  EditProfileSelected: "edit_profile_selected",
  DeleteProfileSelected: "delete_profile_selected",
  AvatarSelected: "avatar_selected",
  SkipToggled: "skip_toggled"
} as const;

export type ProfileActionName =
  (typeof ProfileAction)[keyof typeof ProfileAction];

export type ProfileLogFields = Record<string, string | undefined>;

const EVENT_ITEM_SELECTED = "itemSelected";
const EVENT_TYPE_CLICK = "click";
const EVENT_TYPE_IMPRESSION = "impression";
const EVENT_VIEWED_SCREEN = "viewedScreen";

const KEY_ACTION = "action";
const KEY_PREVIOUS_SCREEN_ID = "previous_screen_id";
const KEY_SCREEN_ID = "screen_id";
const KEY_SCREEN_NAME = "screen_name";

/** Profile management telemetry facade. */
export class ProfileLogger {
  private static mPreviousScreenId: string | undefined = undefined;

  public static logProfileAction(
    action: ProfileActionName,
    screen: ProfileScreenName,
    extra?: ProfileLogFields
  ): void {
    const details: LogDetails = {
      [KEY_ACTION]: action,
      [KEY_SCREEN_ID]: screen
    };
    ProfileLogger.assign(details, extra);

    AnalyticsLogger.trackEvent(EVENT_ITEM_SELECTED, EVENT_TYPE_CLICK, details);
  }

  public static logViewedScreen(
    screenId: ProfileScreenName,
    screenName: string,
    extra?: ProfileLogFields
  ): void {
    const details: LogDetails = {
      [KEY_SCREEN_ID]: screenId,
      [KEY_SCREEN_NAME]: screenName
    };
    if (ProfileLogger.mPreviousScreenId !== undefined) {
      details[KEY_PREVIOUS_SCREEN_ID] = ProfileLogger.mPreviousScreenId;
    }
    ProfileLogger.assign(details, extra);
    ProfileLogger.mPreviousScreenId = screenId;

    AnalyticsLogger.trackEvent(
      EVENT_VIEWED_SCREEN,
      EVENT_TYPE_IMPRESSION,
      details
    );
  }

  public static resetScreenHistory(): void {
    ProfileLogger.mPreviousScreenId = undefined;
  }

  private static assign(target: LogDetails, extra?: ProfileLogFields): void {
    if (extra === undefined) {
      return;
    }
    Object.keys(extra).forEach((key: string): void => {
      const value: string | undefined = extra[key];
      if (value !== undefined) {
        target[key] = value;
      }
    });
  }
}
